/**
 * /well-testing/invoices/:id — one WTI- invoice on its own page
 * (fieldiq-api#1254, WT-5a). Opened from the register, New invoice, a job's
 * Invoices panel or a ticket's "On invoice" link. The back arrow returns to the
 * register with its filters. Pipeline & Facility's page, carried over, with the
 * well in place of the site.
 *
 * EVERY FIGURE IS THE API'S, AS STORED AT GENERATION — lines, subtotal, each
 * levied tax (its stored label and amount), the tax's provenance and the total
 * come from GET /invoices/:id, the loader the PDF and the send also use.
 * Nothing here adds lines up or computes tax. An absent figure reads `—`.
 *
 * ACTIONS (band, top right; admin and office staff — everyone else reads):
 *   draft              PDF · Send · Void
 *   sent / approved    PDF · Send again · Void
 *   paid / void        PDF. Final and read-only.
 * There is no Mark paid in WT-5a — that door is Finance's (WT-5b). Each action
 * opens INLINE under the band (InvoiceActions.tsx), no modal. When the company
 * default priced the tax (`taxRateSource` company_columns), the page says why;
 * a fallback the send refuses (`tax_fallback_blocked`) holds Send and says what
 * to do instead.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Ban, FileText, Send } from 'lucide-react';
import { api, getBlobApiErrorMessage } from '../../../api/client';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { useWtMeta } from '../../../hooks/useWellTesting';
import { useWtInvoice } from '../../../hooks/useWtInvoices';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { catalogLabel, fmtWtNumber, wtDay } from '../../../lib/well-testing';
import { wtRateUnitSuffix, wtScopeText } from '../../../lib/wt-tickets';
import {
  canWriteWtInvoices,
  wtInvoiceIsFinal,
  wtInvoiceIsLive,
  wtInvoiceIsTaxFallback,
  wtInvoiceLineGroups,
  wtInvoiceSendBlocked,
  wtInvoiceStatusSkin,
  wtLeviedTaxRows,
  wtReleasedTicketNumbers,
  wtTaxFallbackText,
  wtTaxRowLabel,
  wtTaxSourceText,
} from '../../../lib/wt-invoices';
import type { WtInvoice } from '../../../types/wt-invoices';
import { useBackToList } from '../../downhole/recordPage';
import { wtRateText } from '../tickets/TicketBits';
import { isNotFound, WT_BAND_SECONDARY, WT_EYEBROW, WT_PRIMARY, WtBadge, WtEmpty, WtFormError, WtLoading, WtPanel, WtSpec } from '../WtBits';
import { WtInvoiceSendPanel, WtInvoiceVoidPanel, WtReleasedNotice } from './InvoiceActions';
import { WT_INVOICES_PATH } from './InvoicesScreen';

const at = (v: string | null | undefined, tz: string | null) => (v ? formatInstant(v, tz) ?? '—' : '—');

/** A line's quantity: `10.5 hr`, `1 day`, `42 km`, `120 m3`. Absent → `—`. */
export const wtInvoiceQtyText = (q: number | null, unit: string, distanceUnit: string | null, volumeUnit: string | null) => {
  const v = fmtWtNumber(q);
  if (v === '—') return v;
  const u = wtRateUnitSuffix(unit, distanceUnit, volumeUnit).replace(/^\//, '').trim();
  return u ? `${v} ${u}` : v;
};

/** Said when the company default priced the tax: amber when the send refuses it, neutral when it is benign. */
export function WtTaxFallbackNotice({ inv }: { inv: WtInvoice }) {
  const text = wtTaxFallbackText(inv);
  if (!text) return null;
  const blocked = wtInvoiceSendBlocked(inv);
  return (
    <div
      role="note"
      data-testid="wt-invoice-tax-fallback"
      data-blocked={blocked ? 'true' : 'false'}
      className={`flex items-start gap-3 rounded-fi-panel border border-fieldiq-border border-l-4 bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text ${blocked ? 'border-l-fi-amber' : 'border-l-fi-steel'}`}
    >
      {blocked && <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-signal-amber" aria-hidden="true" />}
      <div>
        <p className="font-bold">{blocked ? 'This invoice cannot be sent.' : 'Tax from the company settings.'}</p>
        <p className="mt-1">{text}</p>
      </div>
    </div>
  );
}

export default function WtInvoiceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(WT_INVOICES_PATH);
  const { user, company } = useAuth();
  const showsPst = company?.country === 'CA';
  const tz = useTenantTimeZone();
  const meta = useWtMeta().data?.data ?? null;
  const query = useWtInvoice(id);
  const inv = query.data?.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !inv && !query.isLoading && (isNotFound(queryFailureError(query)) || !failure);
  const [panel, setPanel] = useState<'send' | 'void' | null>(null);
  const [released, setReleased] = useState<string[] | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const canWrite = canWriteWtInvoices(user?.role);
  const status = inv?.status ?? null;
  const live = wtInvoiceIsLive(status);
  const sendBlocked = !!inv && wtInvoiceSendBlocked(inv);
  const cur = inv?.currency ?? null;

  const openPdf = async () => {
    if (!inv) return;
    setPdfError(null);
    const win = claimBlobTab();
    if (!win) return setPdfError(POPUP_BLOCKED_MESSAGE);
    setPdfBusy(true);
    try {
      const res = await api.wellTesting.invoices.pdf(inv.id);
      showBlobInTab(win, res.data);
    } catch (e) {
      win.close();
      setPdfError(await getBlobApiErrorMessage(e, 'The PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const actions = inv ? (
    <div className="flex flex-wrap gap-2" data-testid="wt-invoice-actions">
      <button type="button" className={WT_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy} data-testid="wt-invoice-pdf">
        <FileText className="size-4" aria-hidden="true" />
        {pdfBusy ? 'Opening…' : 'PDF'}
      </button>
      {canWrite && live && (
        <button type="button" className={WT_BAND_SECONDARY} onClick={() => setPanel('void')} data-testid="wt-invoice-void">
          <Ban className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {canWrite && live && !sendBlocked && (
        <button type="button" className={WT_PRIMARY} onClick={() => setPanel('send')} data-testid="wt-invoice-send">
          <Send className="size-4" aria-hidden="true" />
          {inv.sentAt ? 'Send again' : 'Send'}
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="wt-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title={inv?.invoiceNumber || (query.isLoading ? 'Invoice' : '—')}
        description={inv ? `${inv.customerName ?? '—'} · ${inv.jobNumber ?? (inv.jobId ? '—' : 'Several jobs')}` : undefined}
        onBack={back}
        backLabel="Back to invoices"
        actions={actions}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="wt-invoice-detail">
          {!inv && query.isLoading ? (
            <WtLoading label="Loading the invoice" />
          ) : notFound ? (
            <WtPanel eyebrow="Invoice" title="Invoice">
              <WtEmpty>This invoice was not found.</WtEmpty>
            </WtPanel>
          ) : !inv ? (
            <WtPanel eyebrow="Invoice" title="Invoice">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this invoice" />
            </WtPanel>
          ) : (
            <>
              <WtFormError message={pdfError} />
              {released && <WtReleasedNotice numbers={wtReleasedTicketNumbers(inv, released)} onDismiss={() => setReleased(null)} />}
              {status === 'void' && (
                <div role="status" data-testid="wt-invoice-void-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">Void {at(inv.voidedAt, tz)}{inv.voidedByName ? ` by ${inv.voidedByName}` : ''}. This invoice is final and read-only.</p>
                  <p className="mt-1 whitespace-pre-wrap">{inv.voidReason || '—'}</p>
                </div>
              )}
              {status === 'paid' && (
                <p role="status" data-testid="wt-invoice-paid-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Paid {at(inv.paidAt, tz)}{inv.paidByName ? `, recorded by ${inv.paidByName}` : ''}. This invoice is final and read-only.
                </p>
              )}
              {!wtInvoiceIsFinal(status) && wtInvoiceIsTaxFallback(inv) && <WtTaxFallbackNotice inv={inv} />}
              {canWrite && live && panel === 'send' && !sendBlocked && <WtInvoiceSendPanel inv={inv} tz={tz} onDone={() => setPanel(null)} />}
              {canWrite && live && panel === 'void' && <WtInvoiceVoidPanel inv={inv} onDone={() => setPanel(null)} onVoided={setReleased} />}

              <WtPanel eyebrow="Invoice" title="Details" testId="wt-invoice-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WtSpec label="Status" value={<span data-testid="wt-invoice-status"><WtBadge skin={wtInvoiceStatusSkin(status)} value={status} /></span>} />
                  <WtSpec label="Invoice date" value={wtDay(inv.invoiceDate)} />
                  <WtSpec label="Due date" value={wtDay(inv.dueDate)} />
                  <WtSpec label="Currency" value={inv.currency || '—'} />
                  <WtSpec label="Job" value={inv.jobId ? <Link className="underline" to={`/well-testing/jobs/${inv.jobId}?tab=tickets`}>{inv.jobNumber || '—'}</Link> : 'Several jobs'} mono />
                  <WtSpec label="Well" value={inv.wellName || '—'} />
                  <WtSpec label={inv.country === 'US' ? 'API number' : 'UWI'} value={inv.wellIdentifier || '—'} mono />
                  <WtSpec label="Surface location" value={inv.surfaceLocation || '—'} mono />
                  <WtSpec label="Province / state" value={inv.jurisdiction || '—'} />
                  <WtSpec label="PO number" value={inv.poNumber || '—'} mono />
                  <WtSpec label="AFE number" value={inv.afeNumber || '—'} mono />
                  <WtSpec label="Cost code" value={inv.costCode || '—'} mono />
                  <WtSpec label="Created" value={`${at(inv.createdAt, tz)}${inv.createdByName ? ` by ${inv.createdByName}` : ''}`} />
                  <WtSpec label="Last sent" value={inv.sentAt ? `${at(inv.sentAt, tz)} to ${inv.sentTo || '—'}` : 'Not sent'} />
                  {inv.sentCc && inv.sentCc.length > 0 && <WtSpec label="Copied to" value={inv.sentCc.join(', ')} />}
                  {showsPst && <WtSpec label="PST exemption" value={inv.pstExempt ? inv.pstExemptNumber || 'Exempt' : '—'} />}
                </dl>
                {inv.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{inv.notes}</p>}
              </WtPanel>

              <WtPanel eyebrow="Bill to" title={inv.customerName || '—'} testId="wt-invoice-customer">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WtSpec label="Billing address" value={inv.customerAddress || '—'} />
                  <WtSpec label="Main contact" value={inv.mainContactName || '—'} />
                  <WtSpec label="Main contact email" value={inv.mainContactEmail || '—'} />
                  <WtSpec label="Accounting email" value={inv.accountingEmail || '—'} />
                </dl>
              </WtPanel>

              <WtPanel eyebrow="Billed" title="Field tickets" testId="wt-invoice-tickets">
                {inv.tickets.length === 0 ? (
                  <WtEmpty>This invoice lists no field tickets.</WtEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {inv.tickets.map((t) => (
                      <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid="wt-invoice-ticket">
                        <Link to={`/well-testing/tickets/${t.id}`} className="font-mono font-extrabold text-white underline underline-offset-2">
                          {t.ticketNumber}
                        </Link>
                        <span className="font-mono text-fieldiq-text">{t.jobNumber || '—'}</span>
                        <span className="text-fieldiq-text">{wtScopeText({ scope: t.scope, workDate: t.workDate })}</span>
                        <span className="text-fieldiq-muted">Approved by {t.customerApprovedByName || '—'}</span>
                        <span className="font-mono text-fieldiq-muted">PO {t.poNumber || '—'}</span>
                        <span className="font-mono text-fieldiq-text">{fmtCurrency(t.subtotal, cur)} before tax</span>
                        {t.releasedAt && <span className="text-fieldiq-muted">Released {at(t.releasedAt, tz)}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </WtPanel>

              <WtPanel eyebrow="Charges" title="Lines" testId="wt-invoice-lines">
                {inv.lines.length === 0 ? (
                  <WtEmpty>This invoice has no lines.</WtEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">Charge</th>
                          <th scope="col" className="px-4 py-3 text-right">Qty</th>
                          <th scope="col" className="px-4 py-3 text-right">Rate</th>
                          <th scope="col" className="px-4 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      {wtInvoiceLineGroups(inv).map((g) => {
                        const t = inv.tickets.find((x) => x.id === g.ticketId);
                        return (
                          <tbody key={g.ticketId} data-testid="wt-invoice-line-group">
                            <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/30">
                              <th scope="colgroup" colSpan={4} className="px-4 py-2 text-left text-xs font-bold text-fieldiq-text">
                                <span className="font-mono">{g.ticketNumber}</span>
                                {t ? ` · ${wtScopeText({ scope: t.scope, workDate: t.workDate })}` : ''}
                                {` · Approved by ${t?.customerApprovedByName || '—'}`}
                              </th>
                            </tr>
                            {g.lines.map((l) => (
                              <tr key={l.id} data-testid="wt-invoice-line" className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                                <td className="px-4 py-3">
                                  <p className="text-sm font-semibold text-white">{l.description || '—'}</p>
                                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                                    {[l.rateCode, l.kindLabel, l.workDate ? wtDay(l.workDate) : null, l.shift ? `${catalogLabel(meta?.shifts, l.shift)} shift` : null].filter(Boolean).join(' · ') || '—'}
                                  </p>
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text" data-testid="wt-invoice-line-qty">{wtInvoiceQtyText(l.quantity, l.unit, l.distanceUnit, l.volumeUnit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text" data-testid="wt-invoice-line-rate">{wtRateText(l.rate, cur, l.unit, l.distanceUnit, l.volumeUnit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-white" data-testid="wt-invoice-line-amount">{fmtCurrency(l.amount, cur)}</td>
                              </tr>
                            ))}
                          </tbody>
                        );
                      })}
                      <tfoot>
                        <tr className="border-t-2 border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-2 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Subtotal</th>
                          <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white" data-testid="wt-invoice-subtotal">{fmtCurrency(inv.subtotal, cur)}</td>
                        </tr>
                        {(() => {
                          const rows = wtLeviedTaxRows(inv);
                          return rows.length > 0 ? (
                            rows.map((r, i) => (
                              <tr key={i} className="border-l-4 border-l-transparent" data-testid="wt-invoice-tax-row">
                                <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">{wtTaxRowLabel(r.label, r.percent)}</th>
                                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(r.amount, cur)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr className="border-l-4 border-l-transparent" data-testid="wt-invoice-tax-row">
                              <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">Tax</th>
                              <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(inv.taxAmount, cur)}</td>
                            </tr>
                          );
                        })()}
                        <tr className="border-l-4 border-l-transparent">
                          <td colSpan={4} className="px-4 pb-2 text-right text-xs text-fieldiq-muted" data-testid="wt-invoice-tax-source">
                            Tax: {wtTaxSourceText(inv)}
                          </td>
                        </tr>
                        <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-3 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Total ({inv.currency || '—'})</th>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-base font-extrabold tabular-nums text-white" data-testid="wt-invoice-total">{fmtCurrency(inv.total, cur)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </WtPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
